/* ============================================================
   /app/install — Add-to-Home-Screen coach screen.

   iOS Safari fires no beforeinstallprompt event at all, so without
   this screen (linked from the invite/magic-link email) an iPhone
   user has no way to discover that the app can be installed. Android
   Chrome does fire beforeinstallprompt, so we offer a one-tap install
   there instead of the manual steps.

   The Android APK (M17, TWA/Bubblewrap) doesn't exist yet — that
   section is a placeholder until M17 ships.
   ============================================================ */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

function isIOS(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isAndroid(): boolean {
  return /android/i.test(navigator.userAgent);
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari's non-standard flag for an already-installed PWA.
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export default function AppInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  async function onAndroidInstallClick() {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center p-8 bg-paper">
      <div className="max-w-sm w-full text-center space-y-6">
        <h1 className="font-display text-2xl font-bold text-ink">
          Install Sahab vCard
        </h1>

        {installed && (
          <p className="text-ink/70 text-sm">
            You're already using the installed app — nothing else to do here.
          </p>
        )}

        {!installed && isIOS() && (
          <ol className="text-left text-sm text-ink/80 space-y-3 list-decimal list-inside">
            <li>
              Tap the <strong>Share</strong> button in Safari's toolbar
              (the square with an arrow pointing up).
            </li>
            <li>
              Scroll down and tap <strong>Add to Home Screen</strong>.
            </li>
            <li>
              Tap <strong>Add</strong> in the top-right corner.
            </li>
          </ol>
        )}

        {!installed && isAndroid() && deferredPrompt && (
          <button
            type="button"
            onClick={onAndroidInstallClick}
            className="w-full rounded-lg bg-ink text-paper font-semibold py-3 text-sm"
          >
            Install app
          </button>
        )}

        {!installed && isAndroid() && !deferredPrompt && (
          <p className="text-ink/70 text-sm">
            Open the browser menu (⋮) and tap <strong>Install app</strong> or{" "}
            <strong>Add to Home screen</strong>.
          </p>
        )}

        {!installed && !isIOS() && !isAndroid() && (
          <p className="text-ink/70 text-sm">
            Open this page on your phone to install the app.
          </p>
        )}

        <p className="text-ink/40 text-xs pt-4 border-t border-ink/10">
          Android APK coming soon.
        </p>

        <Link
          to="/app"
          className="block w-full rounded-lg bg-ink text-paper font-semibold py-3 text-sm"
        >
          {installed ? "Go to my card" : "Continue to my card"}
        </Link>
      </div>
    </main>
  );
}
